import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { FunctionComponent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Stack, Button } from '@mui/material';
import { Roles } from '@/core/types';
import { useSite } from '@/queries/sites';
import { useCustomerSites } from '@/queries/customers';
import ExportButton from '@/features/dashboard/ExportButton';
import CompletedCycles from '@/features/dashboard/widgets/completed-cycles';
import DashboardEmpty from '@/features/dashboard/DashboardEmpty';
import MostActiveUsers from '@/features/dashboard/widgets/statistics/MostActiveUsers';
import MostCleanedLocations from '@/features/dashboard/widgets/statistics/MostCleanedLocations';
import LeviantLogo from '@/assets/logos/LeviantLogo';
import useAuth from '@/shared/context/auth-context';
import { ContentGroup, pushToDataLayer } from '@/shared/utils/analytics';
import { ALL_CUSTOMERS_ID, ALL_SITES_ID } from '@/queries/logs';
import { convertDateFilter, DATE_FILTER_OPTIONS } from '@/features/dashboard/utils';
import { DateFilter, QueryDateRange } from '@/features/dashboard/types';
import { CustomDateRange } from '@/shared/types/date';
import DateRangePicker from '@/components/ui/date-range-picker';
import SelectInput from '@/components/ui/select-input/SelectInput';
import MostActiveDevices from '@/features/dashboard/widgets/statistics/MostActiveDevices';
import AllCycles from '@/features/dashboard/widgets/all-cycles/AllCycles';
import LocationFiltersDialog, {
	SelectedLocation,
} from '@/features/dashboard/widgets/completed-cycles/filters/LocationFiltersDialog';
import { getLocationFilterName } from '../locations/utils';

enum ExportType {
	FullPage = 'FullPage',
	CompletedCycles = 'CompletedCycles',
	AllCycles = 'AllCycles',
	MostActiveUsers = 'MostActiveUsers',
	MostActiveDevices = 'MostActiveDevices',
	MostCleanedLocations = 'MostCleanedLocations',
}

const Dashboard: FunctionComponent = (): ReactElement => {
	const { user } = useAuth();
	const { role } = user || {};
	const isAdmin = role === Roles.LEVIANT_ADMIN;
	const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(ALL_SITES_ID);
	const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(
		ALL_CUSTOMERS_ID
	);
	const [locationsFilter, setLocationsFilter] = useState<SelectedLocation[]>([]);
	const [dateFilter, setDateFilter] = useState<DateFilter>(DateFilter.LAST_7_DAYS);
	const [customRangeFilter, setCustomRangeFilter] = useState<CustomDateRange>({
		start: null,
		end: null,
	});

	const [exportOption, setExportOption] = useState<ExportType | null>(null);
	const [isPrintMode, setIsPrintMode] = useState<boolean>(false);
	const [chartsReady, setChartsReady] = useState<{ [key: string]: boolean }>({
		allCycles: false,
		completedCycles: false,
	});

	const [locationsFilterDialog, setLocationsFilterDialog] = useState(false);

	const { data: customerSites, isFetched: isCustomerSitesFetched } = useCustomerSites();

	const siteId = useMemo(() => {
		if (selectedSiteId) return selectedSiteId;
		const firstCustomerWithSites = customerSites?.find((customer) => customer.sites.length);
		return firstCustomerWithSites?.sites?.[0].id;
	}, [customerSites, selectedSiteId]);

	const locationId = useMemo(() => {
		const firstUnfilledIndex = locationsFilter?.findIndex(
			(selected) => selected?.location === undefined
		);
		if (firstUnfilledIndex === 0) return undefined;
		const lastFilledIndex =
			firstUnfilledIndex === -1 ? locationsFilter.length - 1 : firstUnfilledIndex - 1;
		return locationsFilter?.[lastFilledIndex]?.location?.id;
	}, [locationsFilter]);

	const { query: siteQuery } = useSite(siteId, siteId !== ALL_SITES_ID);
	const { data: siteData } = siteQuery;

	const site = useMemo(() => (siteId === ALL_SITES_ID ? undefined : siteData), [siteData, siteId]);

	const customer = useMemo(() => {
		if (!customerSites || !customerSites.length) return undefined;
		if (!isAdmin) {
			return customerSites[0];
		}
		if (selectedCustomerId === ALL_CUSTOMERS_ID) return undefined;
		return customerSites.find((c) => c.id === selectedCustomerId);
	}, [customerSites, selectedCustomerId, isAdmin]);

	const dateRange: QueryDateRange = useMemo(
		() => convertDateFilter(dateFilter, customRangeFilter),
		[dateFilter, customRangeFilter]
	);

	const showEmptyState = useMemo(
		() => isCustomerSitesFetched && !customerSites?.length,
		[customerSites, isCustomerSitesFetched]
	);

	const handleChartReady = useCallback((chartId: string, ready: boolean) => {
		setChartsReady((prev) => {
			if (prev[chartId] === ready) return prev;
			return { ...prev, [chartId]: ready };
		});
	}, []);

	const updateExportOption = (option: ExportType | null) => {
		setIsPrintMode(true);
		setTimeout(() => {
			setExportOption(option);
		}, 100);
	};

	/* GTM events */
	useEffect(() => {
		if (!exportOption) return;
		const exportEvent = {
			content_group: ContentGroup.Dashboard,
			customer_name: customer?.name,
			site_name: site?.name,
			event: 'dashboard_exported',
		};
		pushToDataLayer(exportEvent);
	}, [customer?.name, exportOption, site?.name]);

	const buttonTitle = useMemo(
		() => getLocationFilterName(selectedCustomerId, selectedSiteId, locationsFilter, customerSites),
		[selectedCustomerId, selectedSiteId, locationsFilter, customerSites]
	);

	useEffect(() => {
		if (isPrintMode && Object.values(chartsReady).every(Boolean)) {
			window.print();
			setIsPrintMode(false);
		}
	}, [isPrintMode, chartsReady]);

	const dashboardHeaderControls = (
		<Stack
			direction="row"
			justifyContent="space-between"
			alignItems="center"
			spacing={2}
			sx={{ width: '100%' }}
		>
			<ExportButton
				label="Export Report"
				type="secondary"
				onClick={() => updateExportOption(ExportType.FullPage)}
				disabled={isPrintMode}
			/>
			<Stack direction="row" spacing={2}>
				<SelectInput
					label="Date range"
					size="small"
					options={DATE_FILTER_OPTIONS}
					value={dateFilter}
					onChange={(e) => setDateFilter(e.target.value as DateFilter)}
					sx={{ width: '220px' }}
				/>
				{dateFilter === DateFilter.CUSTOM_RANGE && (
					<DateRangePicker
						value={customRangeFilter}
						onChange={setCustomRangeFilter}
						variant="outlined"
						size="small"
					/>
				)}
				<Button variant="outlined" onClick={() => setLocationsFilterDialog(true)}>
					{buttonTitle}
				</Button>
			</Stack>
		</Stack>
	);

	const afterPrintHandler = () => {
		setExportOption(null);
		setIsPrintMode(false);
		setChartsReady({ allCycles: false, completedCycles: false });
	};

	useEffect(() => {
		window.addEventListener('afterprint', afterPrintHandler);
		return () => window.removeEventListener('afterprint', afterPrintHandler);
	}, []);

	if (showEmptyState) return <DashboardEmpty />;

	return (
		<AdminPanelPage
			title="Dashboard"
			headerControls={dashboardHeaderControls}
			hideHeaderOnPrint
			className={isPrintMode ? 'print-mode' : undefined}
		>
			<LocationFiltersDialog
				open={locationsFilterDialog}
				onClose={() => setLocationsFilterDialog(false)}
				customerSites={customerSites}
				isAdmin={isAdmin}
				applyLocationFilters={(selectedLocations) => {
					setLocationsFilter(selectedLocations);
				}}
				onCustomerChange={setSelectedCustomerId}
				onSiteChange={setSelectedSiteId}
			/>
			<Grid container spacing={3}>
				<Grid size={12}>
					<Stack
						alignItems="center"
						paddingBottom={2}
						sx={{ display: 'none', displayPrint: 'flex' }}
					>
						<LeviantLogo />
					</Stack>
					<Grid>
						<CompletedCycles
							site={site}
							locationsFilter={locationsFilter}
							locationId={locationId}
							customer={customer}
							exportType={exportOption}
							showAllSitesData={siteId === ALL_SITES_ID}
							isAdmin={isAdmin}
							dateRange={dateRange}
							onChartReady={(ready: boolean) => handleChartReady('completedCycles', ready)}
						/>
					</Grid>
				</Grid>
				<Grid
					size={12}
					sx={{
						display: 'grid',
					}}
				>
					<AllCycles
						site={site}
						locationsFilter={locationsFilter}
						locationId={locationId}
						customer={customer}
						exportType={exportOption}
						showAllSitesData={siteId === ALL_SITES_ID}
						isAdmin={isAdmin}
						dateRange={dateRange}
						onChartReady={(ready: boolean) => handleChartReady('allCycles', ready)}
					/>
				</Grid>
				<Grid
					size={isPrintMode ? 12 : 6}
					sx={{
						display: 'grid',
						pageBreakAfter: 'always',
					}}
				>
					<MostActiveUsers
						site={site}
						locationsFilter={locationsFilter}
						locationId={locationId}
						customer={customer}
						showAllSitesData={siteId === ALL_SITES_ID}
						isAdmin={isAdmin}
						dateRange={dateRange}
					/>
				</Grid>
				<Grid
					size={isPrintMode ? 12 : 6}
					sx={{
						display: 'grid',
						pageBreakAfter: 'always',
					}}
				>
					<MostActiveDevices
						site={site}
						locationsFilter={locationsFilter}
						locationId={locationId}
						customer={customer}
						showAllSitesData={siteId === ALL_SITES_ID}
						isAdmin={isAdmin}
						dateRange={dateRange}
					/>
				</Grid>
				<Grid
					size={12}
					sx={{
						display: 'grid',
					}}
				>
					<MostCleanedLocations
						site={site}
						locationsFilter={locationsFilter}
						locationId={locationId}
						customer={customer}
						isPrintMode={isPrintMode}
						showAllSitesData={siteId === ALL_SITES_ID}
						isAdmin={isAdmin}
						dateRange={dateRange}
					/>
				</Grid>
			</Grid>
		</AdminPanelPage>
	);
};

export default Dashboard;
